import { lazy, Suspense } from "react";
import "@/App.css";
import "@/index.css";

import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";

import { Helmet } from "react-helmet-async";

// Components
import ScrollToTop from "@/components/ui/ScrollToTop";

// Main Pages
import HomePage from "@/pages/HomePage";
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const GetStartedPage = lazy(() => import("@/pages/GetStartedPage"));
const SolutionsPage = lazy(() => import("@/pages/SolutionsPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/legal/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@/pages/legal/TermsOfServicePage"));
const RdspToolForFinancialAdvisorsPage = lazy(() => import("@/pages/services/RdspToolForFinancialAdvisorsPage"));
const CustomDashboardDevelopmentVancouverPage = lazy(() => import("@/pages/services/CustomDashboardDevelopmentVancouverPage"));

// Location Pages
const BurnabyDigitalAgencyPage = lazy(() => import("@/pages/locations/BurnabyDigitalAgencyPage"));
const SurreyDigitalAgencyPage = lazy(() => import("@/pages/locations/SurreyDigitalAgencyPage"));
const RichmondDigitalAgencyPage = lazy(() => import("@/pages/locations/RichmondDigitalAgencyPage"));
const NorthVancouverDigitalAgencyPage = lazy(() => import("@/pages/locations/NorthVancouverDigitalAgencyPage"));
const CoquitlamDigitalAgencyPage = lazy(() => import("@/pages/locations/CoquitlamDigitalAgencyPage"));
const LangleyDigitalAgencyPage = lazy(() => import("@/pages/locations/LangleyDigitalAgencyPage"));

// Service Pages
const WebsiteDevelopmentPage = lazy(() => import("@/pages/services/WebsiteDevelopmentPage"));
const ServicePage = lazy(() => import("@/pages/services/ServicePage"));
const SeoServicesPage = lazy(() => import("@/pages/services/SeoServicesPage"));
const SocialMediaMarketingPage = lazy(() => import("@/pages/services/SocialMediaMarketingPage"));
const MaintenanceSupportPage = lazy(() => import("@/pages/services/MaintenanceSupportPage"));

// Portfolio Pages
const CaseStudiesPage = lazy(() => import("@/pages/portfolio/CaseStudiesPage"));
const GalleryPage = lazy(() => import("@/pages/portfolio/GalleryPage"));
const TestimonialsPage = lazy(() => import("@/pages/portfolio/TestimonialsPage"));
const WorkPage = lazy(() => import("@/pages/portfolio/WorkPage"));

// Company Pages
const AboutPage = lazy(() => import("@/pages/company/AboutPage"));
const ProcessPage = lazy(() => import("@/pages/company/ProcessPage"));
const ValuesPage = lazy(() => import("@/pages/company/ValuesPage"));
const CareersPage = lazy(() => import("@/pages/company/CareersPage"));

// Resources Pages
const FAQsPage = lazy(() => import("@/pages/resources/FAQsPage"));
const FreeResourcesPage = lazy(() => import("@/pages/resources/FreeResourcesPage"));
const NewsPage = lazy(() => import("@/pages/resources/NewsPage"));

/*
 * Local-development redirect:
 * /blogs/example-post -> /blog/example-post
 *
 * In production, .htaccess will also handle this redirect.
 */
function LegacyBlogRedirect() {
  const { id } = useParams();

  return (
    <Navigate
      to={id ? `/blog/${id}` : "/blog"}
      replace
    />
  );
}

function App() {
  const location = useLocation();

  /*
   * These parent URLs are permanently removed.
   *
   * In production, .htaccess returns 410 Gone.
   * These React routes remain as a local-development fallback.
   */
  const noIndexRoutes = ["/company", "/resources"];

  const normalizedPath =
    location.pathname === "/"
      ? "/"
      : location.pathname.replace(/\/+$/, "");

  const shouldNoIndex = noIndexRoutes.includes(normalizedPath);

  return (
    <>
      {/* Global Robots Tag */}
      <Helmet>
        <meta
          name="robots"
          content={
            shouldNoIndex
              ? "noindex, nofollow"
              : "index, follow"
          }
        />
      </Helmet>

      <ScrollToTop />

      <Suspense fallback={<div className="min-h-screen bg-background" aria-busy="true" />} >
      <Routes>
        {/* =====================================================
            MAIN PAGES
        ===================================================== */}

        <Route
          path="/"
          element={<HomePage />}
        />

        <Route
          path="/contact"
          element={<ContactPage />}
        />

        <Route
          path="/get-started"
          element={<GetStartedPage />}
        />

        <Route
          path="/privacy-policy"
          element={<PrivacyPolicyPage />}
        />

        <Route
          path="/terms-of-service"
          element={<TermsOfServicePage />}
        />

        <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
        <Route path="/terms" element={<Navigate to="/terms-of-service" replace />} />

        {/* =====================================================
            SOLUTIONS AND SERVICES OVERVIEW
        ===================================================== */}

        <Route
          path="/solutions"
          element={<SolutionsPage />}
        />

        <Route
          path="/services"
          element={<SolutionsPage />}
        />

        {/* =====================================================
            BLOG
        ===================================================== */}

        <Route
          path="/blog"
          element={<BlogPage />}
        />

        <Route
          path="/blog/:id"
          element={<BlogPage />}
        />

        {/* Old blog URL redirects */}
        <Route
          path="/blogs"
          element={<Navigate to="/blog" replace />}
        />

        <Route
          path="/blogs/:id"
          element={<LegacyBlogRedirect />}
        />

        {/* =====================================================
            RDSP TOOL
        ===================================================== */}

        <Route
          path="/services/rdsp-tool-for-financial-advisors"
          element={<RdspToolForFinancialAdvisorsPage />}
        />

        {/* Old RDSP URL redirect */}
        <Route
          path="/rdsp-tool-for-financial-advisors"
          element={
            <Navigate
              to="/services/rdsp-tool-for-financial-advisors"
              replace
            />
          }
        />

        {/* =====================================================
            CUSTOM DASHBOARD
        ===================================================== */}

        <Route
          path="/services/custom-dashboard-development"
          element={<CustomDashboardDevelopmentVancouverPage />}
        />

        {/* Old Custom Dashboard URL redirect */}
        <Route
          path="/custom-dashboard-development-vancouver"
          element={
            <Navigate
              to="/services/custom-dashboard-development"
              replace
            />
          }
        />

        {/* =====================================================
            LOCATION PAGES
        ===================================================== */}

        <Route
          path="/digital-marketing-agency-surrey"
          element={<SurreyDigitalAgencyPage />}
        />

        <Route
          path="/digital-marketing-agency-burnaby"
          element={<BurnabyDigitalAgencyPage />}
        />

        <Route
          path="/digital-marketing-agency-richmond"
          element={<RichmondDigitalAgencyPage />}
        />

        <Route
          path="/top-digital-agency-in-north-vancouver"
          element={<NorthVancouverDigitalAgencyPage />}
        />

        <Route
          path="/digital-marketing-agency-coquitlam"
          element={<CoquitlamDigitalAgencyPage />}
        />

        <Route
          path="/digital-marketing-agency-langley"
          element={<LangleyDigitalAgencyPage />}
        />

        {/* =====================================================
            SERVICE PAGES — APPROVED URLS
        ===================================================== */}

        <Route
          path="/services/website-development"
          element={<WebsiteDevelopmentPage />}
        />

        <Route
          path="/services/custom-tool-creation"
          element={<ServicePage />}
        />

        <Route
          path="/services/search-engine-optimization-company"
          element={<SeoServicesPage />}
        />

        <Route
          path="/services/social-media-marketing-agency"
          element={<SocialMediaMarketingPage />}
        />

        <Route
          path="/services/website-maintenance-support"
          element={<MaintenanceSupportPage />}
        />

        {/* =====================================================
            OLD SERVICE URL REDIRECTS
        ===================================================== */}

        <Route
          path="/services/website-development-services"
          element={
            <Navigate
              to="/services/website-development"
              replace
            />
          }
        />

        <Route
          path="/services/search-engine-optimization-services"
          element={
            <Navigate
              to="/services/search-engine-optimization-company"
              replace
            />
          }
        />

        <Route
          path="/services/seo-services"
          element={
            <Navigate
              to="/services/search-engine-optimization-company"
              replace
            />
          }
        />

        <Route
          path="/services/social-media-management-services"
          element={
            <Navigate
              to="/services/social-media-marketing-agency"
              replace
            />
          }
        />

        <Route
          path="/services/social-media-management"
          element={
            <Navigate
              to="/services/social-media-marketing-agency"
              replace
            />
          }
        />

        <Route
          path="/services/social-media-marketing"
          element={
            <Navigate
              to="/services/social-media-marketing-agency"
              replace
            />
          }
        />

        <Route
          path="/services/website-maintenance-company"
          element={
            <Navigate
              to="/services/website-maintenance-support"
              replace
            />
          }
        />

        <Route
          path="/services/maintenance-support"
          element={
            <Navigate
              to="/services/website-maintenance-support"
              replace
            />
          }
        />

        <Route
          path="/services/custom-application-development"
          element={
            <Navigate
              to="/services/custom-tool-creation"
              replace
            />
          }
        />

        {/* =====================================================
            PORTFOLIO PAGES
        ===================================================== */}

        <Route
          path="/portfolio/case-studies"
          element={<CaseStudiesPage />}
        />

        <Route
          path="/portfolio/gallery"
          element={<GalleryPage />}
        />

        <Route
          path="/portfolio/testimonials"
          element={<TestimonialsPage />}
        />

        <Route
          path="/work"
          element={<WorkPage />}
        />

        {/* =====================================================
            COMPANY PAGES
        ===================================================== */}

        {/* Permanently removed parent page */}
        <Route
          path="/company"
          element={<NotFoundPage />}
        />

        <Route
          path="/about"
          element={<AboutPage />}
        />

        <Route
          path="/company/process"
          element={<ProcessPage />}
        />

        <Route
          path="/company/values"
          element={<ValuesPage />}
        />

        <Route
          path="/company/careers"
          element={<CareersPage />}
        />

        {/* Old About URL redirect */}
        <Route
          path="/company/about"
          element={<Navigate to="/about" replace />}
        />

        {/* =====================================================
            RESOURCES PAGES
        ===================================================== */}

        {/* Permanently removed parent page */}
        <Route
          path="/resources"
          element={<NotFoundPage />}
        />

        <Route
          path="/resources-faqs"
          element={<FAQsPage />}
        />

        <Route
          path="/resources/free"
          element={<FreeResourcesPage />}
        />

        <Route
          path="/resources/news"
          element={<NewsPage />}
        />

        {/* Old FAQ URL redirects */}
        <Route
          path="/faqs"
          element={
            <Navigate
              to="/resources-faqs"
              replace
            />
          }
        />

        <Route
          path="/resources/faqs"
          element={
            <Navigate
              to="/resources-faqs"
              replace
            />
          }
        />

        {/* =====================================================
            FALLBACK
        ===================================================== */}

        <Route
          path="*"
          element={<NotFoundPage />}
        />
      </Routes>
      </Suspense>
    </>
  );
}

export default App;
